import { createElement } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { renderInlineMarkup } from '@source-components-react/shared/inline-markup';
import {
  ACCORDION_INLINE_STYLES_ATTRIBUTE,
  ACCORDION_LINKS_ATTRIBUTE,
  resolveAccordionDetail,
  resolveAccordionHeadingLevel,
  resolveAccordionOpen,
  resolveAccordionSummary,
} from './accordion-attributes';

/**
 * One `<details>` / `<summary>` disclosure — a summary that expands to its
 * detail — and never a wrapper around it.
 *
 * The element is the whole interaction. Native disclosure gives expand and
 * collapse with no JavaScript at all: it is keyboard operable, it announces
 * its expanded state to a screen reader, the collapsed detail is still in the
 * server's HTML for a search engine to read, and a browser with scripts
 * blocked behaves identically. A div with a click handler would look the same
 * and have none of that, and it would need client state this library does not
 * have — every component here is a pure function.
 *
 * `open` is therefore an attribute rather than state. A disclosure the author
 * wants expanded has to be expanded in the first paint, before hydration,
 * which a `useState` initial value cannot promise.
 *
 * The detail is one paragraph per authored line, the grammar `r-list` uses for
 * its items, with `{n|linked text}` markers resolved against one shared link
 * list. Anchors land inside the paragraph, never around it. The summary takes
 * no links: inside a `<summary>` a click both follows the anchor and toggles
 * the disclosure, so an author cannot get either one reliably.
 *
 * `children` is the nested-content slot: whatever the render path passes lands
 * inside the disclosure, after the authored detail. Composing a subtree into it
 * is platform work that has not shipped yet — see
 * `rulecms/__docs__/__ARCHITECTURE__/ACCORDION_COMPONENT_ARCHITECTURE.md` — so
 * today it is only ever the authored text.
 *
 * Appearance is column styles plus one library stylesheet. Column styles go on
 * the `<details>` element and inherit into the summary and the detail. The
 * sheet hides the UA triangle, puts a chevron on the right, and animates the
 * panel and the chevron on `[open]`. The summary is a full-width flex row so
 * the chevron stays on the right in both states — the same class of hardcoded
 * layout as an embed's aspect ratio.
 */
const SUMMARY_LAYOUT_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  boxSizing: 'border-box',
  cursor: 'pointer',
  listStyle: 'none',
};

const SUMMARY_HEADING_STYLE: CSSProperties = {
  // A heading is `display: block` with its own margins. As a flex item it
  // already shares the summary row with the chevron; zeroing the UA margin
  // keeps the words on that line. `flex: 1` lets a long heading wrap instead
  // of shoving the chevron off the row.
  flex: 1,
  minWidth: 0,
  margin: 0,
};

const CHEVRON_STYLE: CSSProperties = {
  width: 20,
  height: 20,
  flexShrink: 0,
  marginLeft: 'auto',
};

/**
 * Same geometry and stroke as Lucide `chevron-down` (24×24, stroke 2), sized
 * to 20px so it matches the ATL FAQ item. Rotation lives on the stylesheet.
 */
function AccordionChevron() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={24}
      height={24}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={CHEVRON_STYLE}
      data-rulecms-accordion-chevron=""
      aria-hidden="true"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function RAccordion({
  componentAttributes,
  styleProps,
  className,
  children,
}: Readonly<{
  componentAttributes?: { [key: string]: any };
  /** Column styles from the widget pipeline. Always applied to the `<details>` element itself. */
  styleProps?: { [key: string]: any };
  /** Host-app classes for this column. The single root element carries them. */
  className?: string;
  /**
   * Content to render inside the disclosure, after the authored detail. The
   * slot a Composer-authored subtree will occupy; absent on every render path
   * that exists today.
   */
  children?: ReactNode;
}>) {
  const style: CSSProperties | undefined = styleProps;
  const summary = resolveAccordionSummary(componentAttributes);
  const headingLevel = resolveAccordionHeadingLevel(componentAttributes);
  const links = componentAttributes?.[ACCORDION_LINKS_ATTRIBUTE];
  const inlineStyles = componentAttributes?.[ACCORDION_INLINE_STYLES_ATTRIBUTE];

  return (
    <details
      data-rulecms-accordion=""
      style={style}
      {...(className !== undefined ? { className } : {})}
      {...(resolveAccordionOpen(componentAttributes) ? { open: true } : {})}
    >
      <summary style={SUMMARY_LAYOUT_STYLE}>
        {headingLevel ? createElement(headingLevel, { style: SUMMARY_HEADING_STYLE }, summary) : summary}
        <AccordionChevron />
      </summary>
      {resolveAccordionDetail(componentAttributes).map((paragraph, position) => (
        <p key={position}>{renderInlineMarkup(paragraph, links, inlineStyles)}</p>
      ))}
      {children}
    </details>
  );
}
